/**
 * MOTION TOKENS — the single source of timing, easing, distance and scale for
 * every animation in the product.
 *
 * `theme.ts` keeps the CSS-string forms (`duration.fast` = '160ms') that styled
 * components interpolate. This module is the NUMERIC twin those strings are now
 * derived from, plus everything framer-motion needs that a CSS string cannot
 * express: easing arrays, spring configs, distances and scales.
 *
 * Rules that keep the system coherent:
 *  - Micro feedback (press, hover, focus) never exceeds `fast`.
 *  - Component state changes (tab, expand, inline error) sit at `base`.
 *  - Containers (modal, drawer, page) get `emphasis`; nothing decorative goes
 *    past `cinematic`, and `cinematic` is reserved for the two or three moments
 *    that ARE the product (signing in, finishing onboarding).
 *  - Exits are always shorter than entrances. Leaving should feel like letting
 *    go, not like being made to watch something leave.
 */

import type { Transition } from 'framer-motion';

/* ─────────────────────────── DURATION (ms) ─────────────────────────── */

export const DURATION = {
  /** Press feedback, checkbox tick — perceptible but never waited for. */
  instant: 90,
  /** Hover / colour / focus ring. */
  fast: 160,
  /** Default for component state changes. */
  base: 240,
  /** Containers arriving: modal, drawer, page content. */
  emphasis: 360,
  /** Large surfaces and hero moments. */
  cinematic: 560,
  /** How long anything is allowed to take on the way OUT. Same number as
   * `fast` on purpose: the e2e scanner flags any transition duration that is
   * not on the theme scale, so the scale IS the set of legal numbers. */
  exit: 160,
  /** A page's outgoing half — `instant`, so `mode="wait"` never reads as lag. */
  pageExit: 90,
} as const;

export type DurationToken = keyof typeof DURATION;

/* ────────────────────────────── EASING ─────────────────────────────── */

/**
 * Bezier arrays for framer-motion. Their CSS twins live in `theme.easing`;
 * keep the two in step by hand — there is no build step between them.
 */
export const EASE = {
  /** Default state change. */
  standard: [0.4, 0, 0.2, 1] as const,
  /** Content arriving — starts fast, settles slowly. */
  enter: [0.16, 1, 0.3, 1] as const,
  /** Content leaving — accelerates away. */
  exit: [0.4, 0, 1, 1] as const,
  /** Big containers moving — a little more presence in the settle. */
  emphasized: [0.2, 0.8, 0.2, 1] as const,
  /** Press / pop with a gentle overshoot. Never on layout-sized things. */
  overshoot: [0.34, 1.4, 0.5, 1] as const,
} as const;

/* ────────────────────────────── SPRINGS ────────────────────────────── */

/**
 * Physical springs, for things that follow the pointer or move between
 * positions (the nav indicator, a drawer, a sheet). A spring answers an
 * interruption correctly — a re-target mid-flight keeps the current velocity
 * instead of restarting — which is exactly why containers use these and not a
 * tween.
 */
export const SPRING = {
  /** Small things snapping into place: chips, indicators, toggles. */
  snappy: { type: 'spring', stiffness: 520, damping: 38, mass: 0.8 } as const,
  /** The default for containers: fast, one imperceptible overshoot. */
  standard: { type: 'spring', stiffness: 380, damping: 34, mass: 1 } as const,
  /** Drawers and sheets — heavier, settles with authority. */
  gentle: { type: 'spring', stiffness: 260, damping: 30, mass: 1 } as const,
  /** The active-navigation indicator sliding between items. */
  indicator: { type: 'spring', stiffness: 600, damping: 42, mass: 0.7 } as const,
} as const;

/* ───────────────────────────── DISTANCE (px) ───────────────────────── */

/**
 * How far something travels on the way in. Short on purpose — motion reads as
 * "where from", not as a journey. Direction is decided per preset.
 */
export const DISTANCE = {
  micro: 2,
  small: 8,
  medium: 16,
  large: 32,
  hero: 64,
} as const;

/* ─────────────────────────────── SCALE ─────────────────────────────── */

export const SCALE = {
  /** Pressed button. */
  pressed: 0.97,
  /** Hovered card / tile lift companion. */
  hover: 1.015,
  /** A modal on its first frame — close to size, so it reads as focus, not zoom. */
  modalFrom: 0.96,
  /** A popover on its first frame, growing from its anchor. */
  popoverFrom: 0.94,
  /** A success tick's overshoot. */
  pop: 1.08,
} as const;

/* ────────────────────────────── OPACITY ────────────────────────────── */

export const OPACITY = {
  hidden: 0,
  /** A page's first frame — never fully invisible, so no frame is blank. */
  pageFrom: 0.6,
  /** The dim a scrim applies over the page under a modal. */
  scrim: 1,
  /** Refreshing content keeps this much presence while new data lands. */
  refreshing: 0.62,
  visible: 1,
} as const;

/* ─────────────────────────────── BLUR ──────────────────────────────── */

/**
 * Blur is expensive and vestibular; it is used in exactly one place — the
 * scrim behind a modal — and only at the small radius below.
 */
export const BLUR = {
  scrim: 6,
} as const;

/* ───────────────────────────── STAGGER (s) ─────────────────────────── */

export const STAGGER = {
  /** Between list items on first paint. */
  list: 0.035,
  /** Between the sections of a form or a wizard step. */
  section: 0.06,
  /** How many items get a stagger before the rest simply appear. Beyond this
   * a cascade stops helping and starts costing. */
  maxItems: 12,
} as const;

/* ───────────────────────────── HELPERS ─────────────────────────────── */

export type Bezier = readonly [number, number, number, number];

/** Seconds, for framer's `transition.duration`. */
export const sec = (ms: number): number => ms / 1000;

export const tween = (
  ms: number,
  ease: Bezier = EASE.standard,
  delayMs = 0
): Transition => ({
  type: 'tween',
  duration: sec(ms),
  ease: [ease[0], ease[1], ease[2], ease[3]],
  delay: sec(delayMs),
});
